import { Matrix4, type Quaternion } from 'three';

/**
 * Orientation maths for the CSS view cube.
 *
 * The cube shows the scene as the camera sees it, so its rotation is the
 * inverse of the camera's: world -> view. Converting that to CSS costs one
 * extra step, because CSS screen space points Y down while three.js points it
 * up. Flipping Y on both the input and the output — F·R·F, with
 * F = diag(1, -1, 1) — negates the Y row and the Y column, leaving the Y/Y
 * element alone since it gets negated twice.
 */

/** Column-major 4x4 for CSS `matrix3d`, from the camera's world orientation. */
export function cubeRotationMatrix(cameraQuaternion: Quaternion): number[] {
  const e = new Matrix4()
    .makeRotationFromQuaternion(cameraQuaternion.clone().invert())
    .elements;
  return [
    e[0]!, -e[1]!, e[2]!, 0,
    -e[4]!, e[5]!, -e[6]!, 0,
    e[8]!, -e[9]!, e[10]!, 0,
    0, 0, 0, 1,
  ];
}

// Values this small are floating point noise from the quaternion maths, and
// letting them into the style string makes it churn on every frame.
const EPSILON = 1e-6;

export function cssMatrix3d(m: readonly number[]): string {
  const parts = m.map((n) => {
    const rounded = Math.abs(n) < EPSILON ? 0 : n;
    return String(Number(rounded.toFixed(6)));
  });
  return `matrix3d(${parts.join(', ')})`;
}
